import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import AffiliateCallout from "@/components/AffiliateCallout";
import { trackEvent } from "@/lib/analytics";
import { COMPANY } from "@/lib/companyInfo";

// Premium direct-sponsor slot. A signed sponsor takes precedence over the
// generic affiliate callout; with neither configured, nothing renders.
// "Permanent" placements are long-term contracts implemented as env swaps
// at deploy time, so creative changes never require a code release.
const SPONSOR_URL = import.meta.env.VITE_SPONSOR_POST_RESULTS_URL as string | undefined;
const SPONSOR_NAME = import.meta.env.VITE_SPONSOR_POST_RESULTS_NAME as string | undefined;
const SPONSOR_LABEL =
  (import.meta.env.VITE_SPONSOR_POST_RESULTS_LABEL as string | undefined) ??
  "See what our partner recommends for your search";

interface SponsorPlacementProps {
  placement?: string;
}

const SponsorPlacement = ({ placement = "post_results" }: SponsorPlacementProps) => {
  if (!SPONSOR_URL) {
    return <AffiliateCallout />;
  }

  return (
    <Card className="max-w-2xl mx-auto bg-muted/40">
      <CardContent className="pt-6">
        {SPONSOR_NAME && (
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Presented by {SPONSOR_NAME}
          </p>
        )}
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <p className="text-sm text-muted-foreground flex-1">
            The statistics tell you how rare your match is — meeting them is the next
            step. {SPONSOR_LABEL}.
          </p>
          <Button asChild variant="secondary" size="sm">
            <a
              href={SPONSOR_URL}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={() =>
                trackEvent("sponsor_clicked", {
                  placement,
                  sponsor_name: SPONSOR_NAME ?? "unnamed",
                })
              }
            >
              Learn more
              <ExternalLink className="w-3.5 h-3.5 ml-2" />
            </a>
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          Disclosure: this is a paid sponsorship. It costs you nothing and supports the free
          calculator. {COMPANY.brandName} is operated by {COMPANY.legalName}.
        </p>
      </CardContent>
    </Card>
  );
};

export default SponsorPlacement;
